<template>
  <div
    v-if="modelValue"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
    @click.self="close"
  >
    <div class="bg-white w-full max-w-lg rounded-xl shadow-lg p-6">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-lg font-semibold text-gray-800">Add Recognition</h2>
        <button
          @click="close"
          class="text-gray-400 hover:text-gray-600 text-xl"
        >
          &times;
        </button>
      </div>
      <form @submit.prevent="submit" class="space-y-4">
        <div>
          <label class="text-sm text-gray-700">Name of Employee</label>
          <div
            v-if="selectedEmployee"
            class="mt-1 flex items-center gap-3 border rounded-lg px-3 py-2 bg-orange-50 border-orange-200"
          >
            <img
              :src="selectedEmployee.mediaLink"
              :alt="selectedEmployee.name"
              class="w-8 h-8 rounded-full object-cover flex-shrink-0"
              @error="
                (e) =>
                  (e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                    selectedEmployee.name
                  )}&background=f97316&color=fff`)
              "
            />
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-800 truncate">
                {{ selectedEmployee.name }}
              </p>
              <p class="text-xs text-gray-500 truncate">
                {{ selectedEmployee.department }} -
                {{ selectedEmployee.designation }}
              </p>
            </div>
            <button
              type="button"
              @click="clearEmployee"
              class="text-gray-400 hover:text-gray-600 text-lg"
            >
              &times;
            </button>
          </div>
          <div v-else class="relative">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search employee name..."
              class="w-full mt-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300"
              @input="onSearchInput"
              @focus="showDropdown = true"
              autocomplete="off"
            />
            <div v-if="isSearching" class="absolute right-3 top-3.5">
              <svg
                class="animate-spin h-4 w-4 text-orange-400"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                />
              </svg>
            </div>
            <div
              v-if="showDropdown && filteredEmployees.length"
              class="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto"
            >
              <button
                v-for="emp in filteredEmployees"
                :key="emp.id"
                type="button"
                class="w-full flex items-center gap-3 px-3 py-2 hover:bg-orange-50 transition-colors"
                @click="selectEmployee(emp)"
              >
                <img
                  :src="emp.mediaLink"
                  :alt="emp.name"
                  class="w-9 h-9 rounded-full object-cover flex-shrink-0"
                  @error="
                    (e) =>
                      (e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                        emp.name
                      )}&background=f97316&color=fff`)
                  "
                />
                <div class="flex-1 min-w-0 text-left">
                  <p class="text-sm font-medium text-gray-800 truncate">
                    {{ emp.name }}
                  </p>
                  <p class="text-xs text-gray-500 truncate">
                    {{ emp.department }} - {{ emp.designation }}
                  </p>
                </div>
              </button>
            </div>
            <div
              v-if="
                showDropdown &&
                searchQuery.length >= 2 &&
                !isSearching &&
                !filteredEmployees.length
              "
              class="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-3 text-sm text-gray-500 text-center"
            >
              No employees found.
            </div>
          </div>
        </div>
        <div>
          <label class="text-sm text-gray-700">Month of Recognition</label>
          <input
            v-model="form.monthOfRecognition"
            type="month"
            class="w-full mt-1 border rounded-lg px-3 py-2 text-sm"
            required
          />
        </div>
        <div class="flex justify-end gap-2 pt-4">
          <button
            type="button"
            @click="close"
            class="px-4 py-2 text-sm rounded-lg border text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="isSubmitting"
            class="px-4 py-2 text-sm rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50"
          >
            {{ isSubmitting ? "Saving..." : "Save" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { reactive, ref, computed, watch, inject } from "vue";

export default {
  props: {
    modelValue: { type: Boolean, required: true },
  },
  emits: ["update:modelValue", "submit"],
  setup(props, { emit }) {
    const Swal = inject("$swal");
    const axios = inject("$axios");

    const form = reactive({
      employeeId: null,
      employeeName: "",
      monthOfRecognition: "",
    });
    const searchQuery = ref("");
    const allEmployees = ref([]);
    const selectedEmployee = ref(null);
    const showDropdown = ref(false);
    const isSearching = ref(false);
    const isSubmitting = ref(false);
    let searchTimeout = null;

    const filteredEmployees = computed(() => {
      if (searchQuery.value.length < 2) return [];
      const q = searchQuery.value.toLowerCase();
      return allEmployees.value.filter((emp) =>
        emp.name.toLowerCase().includes(q)
      );
    });

    const fetchEmployees = async () => {
      if (allEmployees.value.length > 0) return;
      isSearching.value = true;
      try {
        const { data } = await axios.get("/api/auth/employees", {
          params: {
            status: "employed",
          },
        });
        allEmployees.value = data?.employees || [];
      } catch (e) {
        console.error("Failed to fetch employees", e);
      } finally {
        isSearching.value = false;
      }
    };

    const onSearchInput = () => {
      showDropdown.value = true;
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(fetchEmployees, 300);
    };

    const selectEmployee = (emp) => {
      selectedEmployee.value = emp;
      form.employeeId = emp.id;
      form.employeeName = emp.name;
      searchQuery.value = "";
      showDropdown.value = false;
    };

    const clearEmployee = () => {
      selectedEmployee.value = null;
      form.employeeId = null;
      form.employeeName = "";
    };

    const resetForm = () => {
      form.employeeId = null;
      form.employeeName = "";
      form.monthOfRecognition = "";
      selectedEmployee.value = null;
      searchQuery.value = "";
    };

    const close = () => {
      resetForm();
      emit("update:modelValue", false);
    };

    const handleClickOutside = (e) => {
      if (!e.target.closest(".relative")) showDropdown.value = false;
    };

    watch(
      () => props.modelValue,
      (val) => {
        val
          ? document.addEventListener("click", handleClickOutside)
          : document.removeEventListener("click", handleClickOutside);
      }
    );

    const submit = async () => {
      if (!form.employeeId) {
        return Swal.fire({
          icon: "warning",
          title: "Required",
          text: "Please select an employee.",
          confirmButtonColor: "#f97316",
        });
      }

      isSubmitting.value = true;
      try {
        const { data } = await axios.post("/api/recognitions", {
          employee_id: form.employeeId,
          employee_name: form.employeeName,
          month_of_recognition: form.monthOfRecognition,
        });

        Swal.fire({
          icon: "success",
          title: "Success",
          text: data.message,
          confirmButtonColor: "#f97316",
        });

        emit("submit", {
          record: data.data,
          employee: selectedEmployee.value,
        });

        resetForm();
        close();
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Oops!",
          text:
            error.response?.data?.message ||
            "Something went wrong while saving.",
        });
      } finally {
        isSubmitting.value = false;
      }
    };

    return {
      form,
      close,
      submit,
      searchQuery,
      filteredEmployees,
      selectedEmployee,
      showDropdown,
      isSearching,
      isSubmitting,
      onSearchInput,
      selectEmployee,
      clearEmployee,
    };
  },
};
</script>
